/**
 * Unified Diagram Controls - Toolbar Component
 * Issue #245: Standardize Diagram Controls Across All Renderers
 *
 * A unified toolbar component that adapts to diagram capabilities.
 */

import { memo, useState } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  LayoutGrid,
  Search,
  Focus,
  ChevronDown,
  X,
} from 'lucide-react';
import { useDiagramControlsOptional } from './DiagramControlsContext';
import { FilterChips } from './FilterChips';
import { FilterDropdown } from './FilterDropdown';
import type { LayoutDirection } from './types';

interface UnifiedDiagramToolbarProps {
  className?: string;
  /** Whether to show the toolbar in compact mode */
  compact?: boolean;
  /** Override capabilities dynamically */
  showSearch?: boolean;
  showFocus?: boolean;
  showLayoutDirection?: boolean;
  showAutoLayout?: boolean;
}

const LAYOUT_DIRECTIONS: { value: LayoutDirection; label: string }[] = [
  { value: 'TB', label: 'Top to Bottom' },
  { value: 'LR', label: 'Left to Right' },
  { value: 'BT', label: 'Bottom to Top' },
  { value: 'RL', label: 'Right to Left' },
];

/**
 * Unified toolbar component that adapts based on diagram capabilities
 */
export const UnifiedDiagramToolbar = memo(function UnifiedDiagramToolbar({
  className = '',
  compact = false,
  showSearch: overrideShowSearch,
  showFocus: overrideShowFocus,
  showLayoutDirection: overrideShowLayoutDirection,
  showAutoLayout: overrideShowAutoLayout,
}: UnifiedDiagramToolbarProps) {
  const controls = useDiagramControlsOptional();
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // If not in a provider context, render nothing
  if (!controls) {
    return null;
  }

  const {
    // State
    zoomLevel,
    layoutDirection,
    isLayouting,
    searchQuery,
    isFocusMode,
    activeFilters,
    totalNodeCount,
    filteredNodeCount,
    capabilities,
    // Actions
    fitView,
    zoomIn,
    zoomOut,
    setLayoutDirection,
    applyAutoLayout,
    setSearchQuery,
    toggleFocusMode,
    toggleFilterValue,
    clearFilter,
  } = controls;

  // Determine which controls to show based on capabilities and overrides
  const showSearch = overrideShowSearch ?? capabilities.search;
  const showFocus = overrideShowFocus ?? capabilities.focus;
  const showLayoutDir = overrideShowLayoutDirection ?? capabilities.layoutDirection;
  const showAutoLayoutBtn = overrideShowAutoLayout ?? capabilities.autoLayout;
  const hasFilters = capabilities.filters.length > 0;

  // Stats display
  const hasActiveFilters = Array.from(activeFilters.values()).some(s => s.size > 0);
  const statsText = hasActiveFilters
    ? `${filteredNodeCount}/${totalNodeCount} nodes`
    : `${totalNodeCount} nodes`;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Zoom Controls */}
      <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-1 py-0.5">
        <button
          onClick={zoomOut}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
        <span className="text-xs text-slate-500 dark:text-slate-400 min-w-[3rem] text-center">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700" />
        <button
          onClick={fitView}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Fit View"
        >
          <Maximize2 className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
      </div>

      {/* Layout Controls */}
      {(showLayoutDir || showAutoLayoutBtn) && (
        <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-1 py-0.5">
          {showAutoLayoutBtn && (
            <button
              onClick={applyAutoLayout}
              disabled={isLayouting}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded disabled:opacity-50"
              title="Auto Layout"
            >
              <LayoutGrid className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
          )}
          {showLayoutDir && (
            <div className="relative">
              <button
                onClick={() => setShowLayoutDropdown(!showLayoutDropdown)}
                className="flex items-center gap-1 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                title="Layout Direction"
              >
                <span className="text-xs text-slate-600 dark:text-slate-300">{layoutDirection}</span>
                <ChevronDown className="w-3 h-3 text-slate-500" />
              </button>
              {showLayoutDropdown && (
                <div className="absolute z-50 top-full mt-1 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg py-1 min-w-[140px]">
                  {LAYOUT_DIRECTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        setLayoutDirection(value);
                        setShowLayoutDropdown(false);
                        // Issue #285: Trigger auto-layout when direction changes
                        // Previously, clicking direction buttons only updated state
                        // but didn't actually re-layout the diagram
                        applyAutoLayout();
                      }}
                      className={`
                        w-full px-3 py-1.5 text-left text-sm
                        ${layoutDirection === value
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }
                      `}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search Control */}
      {showSearch && (
        <div className="flex items-center">
          {searchExpanded ? (
            <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-0.5">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search nodes..."
                className="w-32 text-sm bg-transparent border-none focus:outline-none text-slate-700 dark:text-slate-300"
                autoFocus
              />
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchExpanded(false);
                }}
                className="p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-3 h-3 text-slate-400" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSearchExpanded(true)}
              className="p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
              title="Search Nodes"
            >
              <Search className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
          )}
        </div>
      )}

      {/* Focus Mode Toggle */}
      {showFocus && (
        <button
          onClick={toggleFocusMode}
          className={`
            p-1.5 rounded-md border transition-all
            ${isFocusMode
              ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-400'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700'
            }
          `}
          title={isFocusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
        >
          <Focus className="w-4 h-4" />
        </button>
      )}

      {/* Filters */}
      {hasFilters && !compact && (
        <div className="flex items-center gap-2">
          {capabilities.filters.map((filter) => {
            const filterActiveValues = activeFilters.get(filter.id) || new Set();

            if (filter.type === 'chip') {
              return (
                <FilterChips
                  key={filter.id}
                  filter={filter}
                  activeValues={filterActiveValues}
                  onToggle={(value) => toggleFilterValue(filter.id, value)}
                  onClear={() => clearFilter(filter.id)}
                />
              );
            }

            return (
              <FilterDropdown
                key={filter.id}
                filter={filter}
                activeValues={filterActiveValues}
                onToggle={(value) => toggleFilterValue(filter.id, value)}
                onClear={() => clearFilter(filter.id)}
              />
            );
          })}
        </div>
      )}

      {/* Stats */}
      {!compact && (
        <div className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
          {statsText}
          {hasActiveFilters && (
            <span className="text-blue-500 ml-1">(filtered)</span>
          )}
        </div>
      )}
    </div>
  );
});

/**
 * Standalone toolbar that can be used outside of context
 * Useful for testing or simple use cases
 */
export interface StandaloneToolbarProps {
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFitView?: () => void;
  onAutoLayout?: () => void;
  zoomLevel?: number;
  isLayouting?: boolean;
  className?: string;
}

export const StandaloneToolbar = memo(function StandaloneToolbar({
  onZoomIn,
  onZoomOut,
  onFitView,
  onAutoLayout,
  zoomLevel = 1,
  isLayouting = false,
  className = '',
}: StandaloneToolbarProps) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-1 py-0.5">
        <button
          onClick={onZoomOut}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
        <span className="text-xs text-slate-500 dark:text-slate-400 min-w-[3rem] text-center">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button
          onClick={onZoomIn}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700" />
        <button
          onClick={onFitView}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          title="Fit View"
        >
          <Maximize2 className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
      </div>

      {onAutoLayout && (
        <button
          onClick={onAutoLayout}
          disabled={isLayouting}
          className="p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
          title="Auto Layout"
        >
          <LayoutGrid className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>
      )}
    </div>
  );
});
